import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Thermometer,
  ShieldCheck,
  KeyRound,
  Copy,
  Plus,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  Users as UsersIcon,
  CreditCard,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { haccpLicensesApi, usersApi } from '../lib/api';
import api from '../lib/api';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const daysLeft = (iso) => {
  if (!iso) return null;
  try {
    const diff = (new Date(iso).getTime() - Date.now()) / 86400000;
    return Math.max(0, Math.floor(diff));
  } catch {
    return null;
  }
};

export default function HaccpLicensesPage() {
  const { user, refreshUser } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = ['admin', 'amministratore', 'rspp'].includes(user?.role);

  const [status, setStatus] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [delegates, setDelegates] = useState([]);
  const [loading, setLoading] = useState(true);
  // v37b - richieste
  const [pendingRequests, setPendingRequests] = useState([]);
  const [myRequest, setMyRequest] = useState(null);
  const [requestDialog, setRequestDialog] = useState(false);
  const [requestType, setRequestType] = useState('annual');
  const [requestNotes, setRequestNotes] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);

  const [activateKey, setActivateKey] = useState('');
  const [genQty, setGenQty] = useState(1);
  const [genType, setGenType] = useState('admin');
  const [genNote, setGenNote] = useState('');
  const [genDialog, setGenDialog] = useState(false);

  const [stripeQty, setStripeQty] = useState(1);
  const [stripeType, setStripeType] = useState('admin');

  const [searchParams] = useSearchParams();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const promises = [haccpLicensesApi.myStatus()];
      if (isAdmin || isSuperAdmin) {
        promises.push(haccpLicensesApi.list());
        promises.push(haccpLicensesApi.myDelegates());
      }
      if (isAdmin || isSuperAdmin) {
        promises.push(usersApi.getAll());
      }
      const results = await Promise.all(promises);
      setStatus(results[0].data);
      if (isAdmin || isSuperAdmin) {
        setLicenses(Array.isArray(results[1]?.data) ? results[1].data : []);
        setDelegates(Array.isArray(results[2]?.data) ? results[2].data : []);
        setAllUsers(Array.isArray(results[3]?.data) ? results[3].data : []);
      }
      // v37b - richieste
      if (isSuperAdmin) {
        try {
          const r = await api.get('/haccp-licenses/requests?status=pending');
          setPendingRequests(Array.isArray(r.data) ? r.data : []);
        } catch (e) { /* ignore */ }
      }
      if (!isSuperAdmin) {
        try {
          const r = await api.get('/haccp-licenses/requests/my');
          setMyRequest(r.data?.has_request ? r.data : null);
        } catch (e) { /* ignore */ }
      }
    } catch (e) {
      console.error(e);
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const submitRequest = async () => {
    setSubmittingRequest(true);
    try {
      const r = await api.post('/haccp-licenses/requests', {
        subscription_type: requestType,
        notes: requestNotes || null,
      });
      toast.success(r.data?.message || 'Richiesta inviata');
      setRequestDialog(false);
      setRequestNotes('');
      const me = await api.get('/haccp-licenses/requests/my');
      setMyRequest(me.data?.has_request ? me.data : null);
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSubmittingRequest(false);
    }
  };

  const approveRequest = async (reqId) => {
    try {
      await api.post(`/haccp-licenses/requests/${reqId}/approve?duration_days=365`);
      toast.success('Richiesta approvata e licenza attivata');
      const r = await api.get('/haccp-licenses/requests?status=pending');
      setPendingRequests(Array.isArray(r.data) ? r.data : []);
      await fetchAll();
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  const rejectRequest = async (reqId) => {
    const reason = window.prompt('Motivazione (opzionale):') || '';
    try {
      await api.post(`/haccp-licenses/requests/${reqId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Richiesta rifiutata');
      const r = await api.get('/haccp-licenses/requests?status=pending');
      setPendingRequests(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stripe redirect handling
  useEffect(() => {
    const stripeStatus = searchParams.get('stripe');
    const sessionId = searchParams.get('session_id');
    if (stripeStatus === 'success' && sessionId) {
      (async () => {
        try {
          const res = await haccpLicensesApi.stripeConfirm(sessionId);
          if (res.data.paid) {
            toast.success('Pagamento riuscito! Licenza HACCP attivata');
            if (refreshUser) await refreshUser();
            await fetchAll();
          } else {
            toast.warning('Pagamento in elaborazione');
          }
        } catch (e) {
          toast.error('Errore conferma pagamento');
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleActivate = async () => {
    if (!activateKey.trim()) {
      toast.error('Inserisci una chiave licenza');
      return;
    }
    try {
      const res = await haccpLicensesApi.activate(activateKey.trim());
      toast.success(res.data.message || 'Licenza HACCP attivata');
      setActivateKey('');
      if (refreshUser) await refreshUser();
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore attivazione');
    }
  };

  const handleGenerate = async () => {
    try {
      const res = await haccpLicensesApi.generate({
        quantity: parseInt(genQty, 10) || 1,
        license_type: genType,
        duration_days: 365,
        note: genNote || null,
      });
      toast.success(`Generate ${res.data.generated} chiavi`);
      setGenDialog(false);
      setGenNote('');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione');
    }
  };

  const handleStripeCheckout = async () => {
    try {
      const res = await haccpLicensesApi.stripeCheckout(stripeType, stripeQty);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore checkout Stripe');
    }
  };

  const handleToggleDelegate = async (uid, enable) => {
    try {
      await haccpLicensesApi.delegate(uid, enable);
      toast.success(enable ? 'Delega attivata' : 'Delega revocata');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore delega');
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copiato!'));
  };

  const delegateCandidates = useMemo(() => {
    const delegableRoles = ['consulente_haccp', 'collaborator', 'gerente', 'azienda', 'laboratorio_analisi'];
    return allUsers
      .filter((u) => delegableRoles.includes(u.role))
      .filter((u) => isSuperAdmin || u.created_by === user?.id);
  }, [allUsers, isSuperAdmin, user?.id]);

  const delegateIds = useMemo(() => new Set(delegates.map((d) => d.id)), [delegates]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const expiresIn = daysLeft(status?.expires_at);
  const isExpiringSoon = expiresIn !== null && expiresIn <= 30;
  const isExpired = status?.access_level === 'readonly';

  return (
    <div data-testid="haccp-licenses-page" className="space-y-6">
      <div className="page-header">
        <h1 className="page-title flex items-center gap-3">
          <Thermometer className="w-8 h-8 text-orange-500" />
          Licenze HACCP
        </h1>
        <p className="page-subtitle">
          Modulo Autocontrollo HACCP — Gestione abbonamenti e deleghe
        </p>
      </div>

      {/* Status card */}
      <Card className="border-orange-200 dark:border-orange-900/40">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-base font-semibold">
              <ShieldCheck className="w-5 h-5 text-orange-500" />
              Stato del tuo accesso HACCP
            </span>
            <Badge variant={status?.access_level === 'admin' ? 'default' : status?.access_level === 'readonly' ? 'destructive' : 'secondary'}>
              {status?.access_level === 'admin' && 'ATTIVO'}
              {status?.access_level === 'readonly' && 'SCADUTA — Sola Lettura'}
              {status?.access_level === 'none' && 'NON ATTIVA'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {status?.access_level === 'admin' && (
            <>
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>Scade il <strong>{formatDate(status?.expires_at)}</strong></span>
                  {expiresIn !== null && (
                    <span className={`text-xs ${isExpiringSoon ? 'text-amber-600 font-semibold' : 'text-muted-foreground'}`}>
                      ({expiresIn} giorni rimanenti)
                    </span>
                  )}
                </div>
                {status?.license_key && (
                  <div className="flex items-center gap-2 font-mono text-xs">
                    <KeyRound className="w-4 h-4 text-muted-foreground" />
                    {status.license_key}
                  </div>
                )}
                {isAdmin && (
                  <div className="flex items-center gap-2 text-sm">
                    <UsersIcon className="w-4 h-4 text-muted-foreground" />
                    <span>{status?.delegates_count || 0} delegati attivi</span>
                  </div>
                )}
              </div>
              {status?.inherited_from && (
                <p className="text-xs text-muted-foreground">
                  Licenza ereditata da <strong>{status.inherited_from.full_name}</strong> ({status.inherited_from.email})
                </p>
              )}
            </>
          )}
          {isExpired && (
            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-900 dark:bg-red-950/40 dark:border-red-900/50 dark:text-red-200">
              <strong>Licenza scaduta:</strong> puoi consultare i dati HACCP ma non puoi più crearne di nuovi. Attiva una nuova chiave o rinnova via Stripe.
            </div>
          )}
          {status?.access_level === 'none' && (
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900 dark:bg-amber-950/40 dark:border-amber-900/50 dark:text-amber-200">
              <AlertTriangle className="inline w-4 h-4 mr-1" />
              Il modulo Autocontrollo HACCP non è attivo. {isAdmin && 'Attiva una chiave o acquista la licenza qui sotto.'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* v37b - Richiesta licenza (per utenti azienda) */}
      {!isAdmin && !isSuperAdmin && (
        <Card className="border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              Richiedi attivazione licenza HACCP
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myRequest && myRequest.status === 'pending' && (
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm space-y-1">
                <div className="font-semibold text-amber-900">⏳ Richiesta in attesa di approvazione</div>
                <div className="text-amber-800">
                  Inviata il {formatDate(myRequest.created_at)} · Abbonamento: {myRequest.subscription_type}
                </div>
              </div>
            )}
            {myRequest && myRequest.status === 'approved' && (
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm">
                <CheckCircle2 className="inline w-4 h-4 mr-1 text-emerald-700" />
                <span className="font-semibold text-emerald-900">Richiesta approvata il {formatDate(myRequest.approved_at)}</span>
              </div>
            )}
            {myRequest && myRequest.status === 'rejected' && (
              <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm">
                <div className="font-semibold text-red-900">Richiesta non approvata</div>
                {myRequest.reject_reason && <div className="text-red-800">Motivazione: {myRequest.reject_reason}</div>}
                <Button size="sm" variant="outline" className="mt-2" onClick={() => setRequestDialog(true)}>
                  Invia nuova richiesta
                </Button>
              </div>
            )}
            {(!myRequest || (myRequest.status !== 'pending' && myRequest.status !== 'approved')) && (
              <>
                <p className="text-sm text-muted-foreground mb-3">
                  Per gestire il modulo HACCP della tua UL alimentare invia una richiesta al CEN Engineering.
                  Riceverai un'email con la conferma di attivazione una volta approvata.
                </p>
                <Button onClick={() => setRequestDialog(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="request-haccp-btn">
                  <Sparkles className="w-4 h-4 mr-2" /> Richiedi attivazione
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* v37b - Richieste pendenti (super_admin) */}
      {isSuperAdmin && (
        <Card className="border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              Richieste pendenti ({pendingRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingRequests.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessuna richiesta in attesa.</p>
            ) : (
              <div className="space-y-2">
                {pendingRequests.map((r) => (
                  <div key={r.id} className="border rounded p-3 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`haccp-request-${r.id}`}>
                    <div>
                      <div className="font-semibold">{r.azienda_name || r.user_full_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.user_full_name} · {r.user_email} · {r.subscription_type} · {formatDate(r.created_at)}
                      </div>
                      {r.notes && <div className="text-xs italic text-gray-600 mt-1">"{r.notes}"</div>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => approveRequest(r.id)} data-testid={`approve-${r.id}`}>
                        Approva (365gg)
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-400 text-red-700 hover:bg-red-50"
                              onClick={() => rejectRequest(r.id)} data-testid={`reject-${r.id}`}>
                        Rifiuta
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Activate key (for admins) */}
      {isAdmin && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-emerald-600" />
              Attiva una chiave licenza
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="HACCP-XXXX-XXXX-XXXX"
                value={activateKey}
                onChange={(e) => setActivateKey(e.target.value.toUpperCase())}
                className="font-mono"
                data-testid="haccp-license-key-input"
              />
              <Button onClick={handleActivate} data-testid="haccp-activate-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Attiva
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Se hai più chiavi, attivandone una nuova la durata si somma a quella esistente.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Stripe checkout (for admins) */}
      {isAdmin && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Acquista licenza HACCP
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="space-y-1">
                <Label className="text-xs">Tipo</Label>
                <select
                  value={stripeType}
                  onChange={(e) => setStripeType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm bg-background"
                >
                  <option value="admin">Admin (99 €/anno)</option>
                  <option value="delegate">Delegato (49 €/anno)</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Quantità</Label>
                <Input
                  type="number" min={1} max={10}
                  value={stripeQty}
                  onChange={(e) => setStripeQty(parseInt(e.target.value, 10) || 1)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Totale</Label>
                <div className="px-3 py-2 border rounded-md text-sm font-semibold bg-muted">
                  €{((stripeType === 'admin' ? 99 : 49) * stripeQty).toFixed(2)}
                </div>
              </div>
            </div>
            <Button onClick={handleStripeCheckout} variant="default" className="w-full sm:w-auto" data-testid="haccp-stripe-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              Procedi al pagamento sicuro
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Pagamento gestito da Stripe. Riceverai N chiavi licenza e la prima sarà attivata automaticamente.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Delegati (per admin con licenza attiva) */}
      {isAdmin && status?.access_level === 'admin' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <UsersIcon className="w-5 h-5 text-indigo-600" />
              Delega licenza ai tuoi subordinati
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              I tuoi consulenti HACCP, collaboratori e gerenti ereditano la tua licenza solo se attivi la delega.
            </p>
          </CardHeader>
          <CardContent>
            {delegateCandidates.length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">
                Non hai ancora creato subordinati. Vai su "Utenti" per crearli.
              </p>
            )}
            <div className="divide-y divide-border">
              {delegateCandidates.map((u) => {
                const enabled = delegateIds.has(u.id);
                return (
                  <div key={u.id} className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium">{u.full_name}</p>
                      <p className="text-xs text-muted-foreground">{u.email} · {u.role}</p>
                    </div>
                    <Button
                      variant={enabled ? 'secondary' : 'default'}
                      size="sm"
                      onClick={() => handleToggleDelegate(u.id, !enabled)}
                      data-testid={`delegate-toggle-${u.id}`}
                    >
                      {enabled ? 'Revoca' : 'Delega'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Super admin: genera chiavi */}
      {isSuperAdmin && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-orange-600" />
                Genera nuove chiavi (Super Admin)
              </span>
              <Dialog open={genDialog} onOpenChange={setGenDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="haccp-gen-keys-btn">
                    <Plus className="w-4 h-4 mr-1" /> Genera Chiavi
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Genera chiavi licenza HACCP</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div>
                      <Label>Quantità</Label>
                      <Input type="number" min={1} max={100} value={genQty} onChange={(e) => setGenQty(e.target.value)} />
                    </div>
                    <div>
                      <Label>Tipo licenza</Label>
                      <select value={genType} onChange={(e) => setGenType(e.target.value)} className="w-full px-3 py-2 border rounded-md text-sm bg-background">
                        <option value="admin">Admin (per amministratore/RSPP)</option>
                        <option value="delegate">Delegato (per subordinati)</option>
                      </select>
                    </div>
                    <div>
                      <Label>Nota (opzionale)</Label>
                      <Input value={genNote} onChange={(e) => setGenNote(e.target.value)} placeholder="es. Cliente XYZ" />
                    </div>
                    <Button onClick={handleGenerate} className="w-full">Genera</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardTitle>
          </CardHeader>
        </Card>
      )}

      {/* Tabella licenze */}
      {(isAdmin || isSuperAdmin) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Chiavi {isSuperAdmin ? 'tutte' : 'attivate da te'} ({licenses.length})</CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left">Chiave</th>
                    <th className="px-2 py-2 text-left">Tipo</th>
                    <th className="px-2 py-2 text-left">Stato</th>
                    <th className="px-2 py-2 text-left">Scade</th>
                    <th className="px-2 py-2 text-left">Nota</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {licenses.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">Nessuna chiave</td></tr>
                  )}
                  {licenses.map((l) => (
                    <tr key={l.id} className="hover:bg-muted/30">
                      <td className="px-4 py-2 font-mono">{l.license_key}</td>
                      <td className="px-2 py-2">
                        <Badge variant="outline">{l.license_type}</Badge>
                      </td>
                      <td className="px-2 py-2">
                        <Badge variant={l.status === 'consumed' ? 'default' : l.status === 'active' ? 'secondary' : 'destructive'}>
                          {l.status === 'active' ? 'Disponibile' : l.status === 'consumed' ? 'In uso' : l.status}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-xs">{l.expires_at ? formatDate(l.expires_at) : '—'}</td>
                      <td className="px-2 py-2 text-xs text-muted-foreground">{l.note || '—'}</td>
                      <td className="px-2 py-2">
                        <Button size="icon" variant="ghost" onClick={() => copy(l.license_key)} title="Copia">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog richiesta licenza */}
      <Dialog open={requestDialog} onOpenChange={setRequestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Richiedi licenza HACCP</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Tipo abbonamento</Label>
              <select
                value={requestType}
                onChange={(e) => setRequestType(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-background"
                data-testid="request-subscription-type"
              >
                <option value="monthly">Mensile</option>
                <option value="annual">Annuale</option>
                <option value="custom">Personalizzato (da concordare)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Note aggiuntive (opzionale)</Label>
              <textarea
                value={requestNotes}
                onChange={(e) => setRequestNotes(e.target.value)}
                rows={3}
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-background"
                placeholder="Es. attivare per la UL Pizzeria Elite di Catania"
                data-testid="request-notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setRequestDialog(false)}>Annulla</Button>
              <Button onClick={submitRequest} disabled={submittingRequest}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="submit-request-btn">
                {submittingRequest ? 'Invio...' : 'Invia richiesta'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
